import { Link } from "react-router-dom";
import { memo } from "react";
import { FaInstagram, FaYoutube, FaFacebookF, FaTwitter, FaLinkedinIn } from "react-icons/fa";
import logo from "../../assets/Logo.avif";

// Social Icons
const SocialIcons = memo(() => {
  const socialLinks = [
    { href: "https://www.linkedin.com/company/3de-tech-proto/", icon: <FaLinkedinIn />, label: "LinkedIn" },
    { href: "https://www.instagram.com/3detechproto/", icon: <FaInstagram />, label: "Instagram" },
    { href: "https://www.youtube.com", icon: <FaYoutube />, label: "YouTube" },
    { href: "https://twitter.com", icon: <FaTwitter />, label: "Twitter" },
    { href: "https://www.facebook.com", icon: <FaFacebookF />, label: "Facebook" },
  ];

  return (
    <div className="flex gap-3">
      {socialLinks.map((social) => (
        <a
          key={social.label}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          className="w-8 h-8 bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-white rounded-full transition-colors"
          aria-label={social.label}
        >
          {social.icon}
        </a>
      ))}
    </div>
  );
});

// Quick Links
const QuickLinks = memo(() => {
  const links = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Offer", path: "/extra-discount" },
  ];

  return (
    <div>
      <h4 className="font-medium text-gray-300 mb-3">Quick Links</h4>
      <ul className="space-y-1">
        {links.map((link) => (
          <li key={link.name}>
            <Link
              to={link.path}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {link.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
});

// Services Links
const ServicesLinks = memo(() => {
  const services = [
    "Vacuum Casting",
    "contractManufacturing",
    "SLA 3D Printing",
    "SLS 3D Printing",
    "DLP 3D Printing",
    "FDM 3D Printing",
  ];

  return (
    <div>
      <h4 className="font-medium text-gray-300 mb-3">Services</h4>
      <ul className="space-y-1">
        {services.map((service) => (
          <li key={service}>
            <Link
              to={`/${service.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-gray-400 hover:text-white text-sm transition-colors"
            >
              {service}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
});

// Contact Info
const ContactInfo = memo(() => {
  const contactDetails = [
    { type: "phone", content: "+91 99945 23799, +91 96554 42269" },
    { type: "address", content: "No. 5, SF No. 16/2A1, SIDCO Industrial Estate, Zuzuvadi, Sipcot Phase I, Hosur, TN 635126" },
    { type: "address", content: "105 Cecil St, #11-00 The Octagon, Singapore 069534" },
    { type: "email", content: "info@3dtechproto.com", href: "mailto:info@3dtechproto.com" },
    { type: "email", content: "salessupport@3dtechproto.com", href: "mailto:salessupport@3dtechproto.com" },
  ];

  return (
    <div>
      <h4 className="font-medium text-gray-300 mb-3">Contact Us</h4>
      <div className="space-y-2 text-gray-400 text-sm">
        {contactDetails.map((detail, index) => (
          <p key={index} className="leading-relaxed">
            {detail.href ? (
              <a href={detail.href} className="text-gray-300 hover:text-white transition-colors">
                {detail.content}
              </a>
            ) : (
              detail.content
            )}
          </p>
        ))}
      </div>
    </div>
  );
});

// Logo Section
const LogoSection = memo(() => (
  <div className="flex-1 max-w-xs">
    <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-block">
      <img src={logo} alt="3DE Logo" width={160} height={48} className="h-12 w-auto" loading="lazy" decoding="async" />
    </Link>
    <div className="mt-3">
      <h3 className="text-lg font-semibold text-gray-300">3DE TECH PROTOTYPE</h3>
      <p className="text-gray-400 mt-1 text-sm">Innovative 3D Printing</p>
    </div>
  </div>
));

// Legal Links
const LegalLinks = memo(() => {
  const legalItems = ["Privacy", "Terms", "Disclaimer"];

  return (
    <div className="flex flex-wrap justify-center gap-4 text-gray-400 text-sm">
      {legalItems.map((item) => (
        <Link
          key={item}
          to={`/${item.toLowerCase()}`}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="hover:text-white transition-colors"
        >
          {item}
        </Link>
      ))}
    </div>
  );
});

// Footer
const Footer = () => (
  <footer className="bg-gray-900 text-gray-200 border-t border-gray-700">
    <div className="px-4 py-8 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        <LogoSection />
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickLinks />
          <ServicesLinks />
          <ContactInfo />
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-700">
        <div className="flex flex-col items-center gap-4">
          <SocialIcons />
          <LegalLinks />
          <p className="text-gray-500 text-xs text-center">© 2019 - 2025 3DE Tech Proto. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  </footer>
);

export default memo(Footer);
